import { Avatar } from "@dropbox/dig-components/avatar";
import { Button } from "@dropbox/dig-components/buttons";
import { Card } from "@dropbox/dig-components/card";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Cluster } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { PersonMultipleLine } from "@dropbox/dig-icons/assets";
import { getInitials } from "@mirage/shared/account";
import i18n from "@mirage/translations";
import React from "react";

import styles from "./ProfileCard.module.css";

interface ProfileCardProps {
  user: {
    name: string;
    role?: string;
    profilePhotoUrl?: string;
    isDeactivated?: boolean;
  };
  isRecentCollaborator?: boolean;
  onFollowClick?: () => void;
  isFollowing?: boolean;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  user,
  isRecentCollaborator = false,
  onFollowClick,
  isFollowing = false,
}) => {
  const initials = getInitials(user.name);
  const userPhotoUrl = user.profilePhotoUrl;

  return (
    <Card
      borderRadius="Large"
      hasBorders={false}
      className={styles.profileCard}
    >
      <Card.Content spacing="medium" className={styles.cardContent}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          rowGap="Micro Large"
        >
          {/* Avatar Section */}
          <Box className={styles.avatarContainer}>
            <Avatar
              src={userPhotoUrl || ""}
              size="large"
              alt={user.name}
              isInactive={user.isDeactivated}
              className={styles.avatar}
            >
              {initials}
            </Avatar>
          </Box>

          {/* User Info Section */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            rowGap="Micro Small"
            className={styles.userInfoContainer}
          >
            {/* Name */}
            <Text size="large" isBold className={styles.nameText}>
              {user.name}
            </Text>

            {/* Role */}
            {user.role && (
              <Text size="small" className={styles.roleText}>
                {user.role}
              </Text>
            )}

            {/* Recent Collaborator Badge */}
            {isRecentCollaborator && (
              <Cluster gap="Micro Small" alignX="center">
                <Cluster.Item>
                  <Box className={styles.recentCollaboratorIcon}>
                    <UIIcon src={PersonMultipleLine} />
                  </Box>
                </Cluster.Item>
                <Cluster.Item>
                  <Text size="small" className={styles.recentCollaboratorText}>
                    {i18n.t("profile_card_recent_collaborator")}
                  </Text>
                </Cluster.Item>
              </Cluster>
            )}
          </Box>

          {/* Follow Button */}
          {onFollowClick && (
            <Box className={styles.buttonContainer}>
              <Button
                variant={isFollowing ? "primary" : "opacity"}
                size="medium"
                onClick={onFollowClick}
                className={styles.followButton}
              >
                {isFollowing
                  ? i18n.t("profile_card_following_button")
                  : i18n.t("profile_card_follow_button")}
              </Button>
            </Box>
          )}
        </Box>
      </Card.Content>
    </Card>
  );
};
